import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchDataFromApi } from '../../helpers/api';
import { getCurrentToken } from '../../utility/getCurrentToken';
import { logout } from '../slices/authSlice';
import { resetImages } from './imagesSlice';
import { resetFlavors } from './flavorsSlice';
import { resetOverview } from './overviewSlice';
import axiosInstance from '../../utility/axiosInstance';
import {handleAxiosError} from "../../helpers/handleAxiosError";

export interface Instance {
    id: string;
    name: string;
    status: string;
    image_id: string;
    flavor_id: string;
    created: string;
    updated: string;
    launched_at?: string | null;
    state: string;
    task: string;
    isPublic?: string;
    access_point?: string | null;
    accessIPv6?: string | null;
    user_id?: string | null;
    project_id?: string | null;
    key_name?: string | null;
    provisioning_mode?: 'free' | 'paid';
    free_resource_kind?: 'app' | 'postgres';
    traffic_paused?: boolean;
    // Set by the backend when this VM is a cluster member - the UI tints these rows + shows a
    // cluster badge. Covers BOTH families: app-cluster members (Heat stamps OS::stack_name /
    // OS::stack_id) and managed-datastore HA members (stamped `cluster_member` + `cluster_role`).
    cluster_member?: boolean;
    cluster_name?: string | null;
    cluster_id?: string | null;
    /** The member's own nested Heat stack (`<parent>-asg-<hash>`). Structural; cluster_name is the label. */
    cluster_stack?: string | null;
    /** "postgres-ha" | "clickhouse-ha" for a managed datastore cluster; null for an app cluster. */
    cluster_role?: string | null;
    /** The cluster this VM is currently the hero of, or null. Exactly one member per cluster. */
    hero_of?: string | null;
    /** EXPERIMENT (prepared machines): set by the backend on a VM started early for a deploy and not claimed yet, or on
     *  a duplicate server a retried create left for one (duplicate: true). */
    prepared_machine?: { machine_id: number; state?: string; duplicate?: boolean; note?: string } | null;
    metadata?: {
        SSHEnabled?: string;
        dns?: string;
        floatinIp?: string;
        isPublic?: string;
        volume?: string;
        imageName?: string;
        tag?: string;
        [key: string]: string | undefined;
    },
    addresses?: {
        [networkName: string]: Array<{
            addr: string;
            type: string;
            mac_addr?: string;
            port?: string;
            "OS-EXT-IPS:type"?: "fixed" | "floating";
            [key: string]: any;
        }>;
    };
    security_groups?: string[];
    tags?: string[];
    [key: string]: any;
}

interface InstancesState {
    data: Instance[];
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

const initialState: InstancesState = {
    data: [],
    status: 'idle',
    error: null,
};

export const fetchInstances = createAsyncThunk(
    'instances/fetchInstances',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get('/v1/instances');
            return response.data;
        } catch (error) {
            console.error("Failed to fetch DNS:", error);
            return rejectWithValue(handleAxiosError(error));
        }
    }
);

const instancesSlice = createSlice({
    name: 'instances',
    initialState,
    reducers: {
        setInstances: (state, action: PayloadAction<Instance[]>) => {
            state.data = action.payload;
        },
        resetInstances: () => initialState,
        updateInstance: (state, action: PayloadAction<Instance>) => {
            const index = state.data.findIndex(
                (instance) => instance.id === action.payload.id
            );

            if (index !== -1) {
                state.data[index] = action.payload;
            } else {
                state.data.push(action.payload);
            }
        },
        removeInstance: (state, action: PayloadAction<{ id: string }>) => {
            state.data = state.data.filter(instance => instance.id !== action.payload.id);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchInstances.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchInstances.fulfilled, (state, action: PayloadAction<Instance[]>) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchInstances.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload as string;
            });
    },
});

export const { setInstances, resetInstances, updateInstance, removeInstance } = instancesSlice.actions;
export default instancesSlice.reducer;
